import { useEffect, useState } from "react";
import { api, formatError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Inbox, Mail, Phone } from "lucide-react";

const STATUSES = ["new", "contacted", "closed"];

export default function EnquiriesPage() {
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    const [e, p] = await Promise.all([api.get("/enquiries"), api.get("/projects")]);
    setItems(e.data); setProjects(p.data);
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/enquiries/${id}?status=${status}`);
      toast.success(`Marked ${status}`);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const list = filter === "all" ? items : items.filter((i) => i.status === filter);
  const projOf = (id) => projects.find((p) => p.id === id);

  return (
    <div className="p-6 md:p-10 max-w-5xl">
      <div className="flex items-center gap-3 mb-2">
        <Inbox className="text-primary" />
        <h1 className="heading text-3xl md:text-4xl">Enquiries</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-6">Leads submitted through the "Enquire" button on each project card.</p>

      <div className="flex flex-wrap gap-2 mb-4">
        {["all", ...STATUSES].map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-md border ${filter === s ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`} data-testid={`enq-filter-${s}`}>
            {s}
          </button>
        ))}
      </div>

      <div className="border border-border rounded-lg bg-card divide-y divide-border">
        {list.length === 0 && <div className="p-6 text-sm text-muted-foreground">No enquiries yet.</div>}
        {list.map((e) => (
          <div key={e.id} className="p-4 flex flex-wrap items-start justify-between gap-4" data-testid={`enq-${e.id}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <div className="text-sm font-medium">{e.name}</div>
                <span className={`status-pill status-${e.status === "closed" ? "completed" : e.status === "contacted" ? "in_progress" : "pending"}`}>{e.status}</span>
                {e.emailed && <span className="text-[10px] uppercase tracking-widest text-muted-foreground">· emailed</span>}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">Project · {projOf(e.project_id)?.name || "—"}</div>
              <div className="flex flex-wrap gap-4 text-xs text-muted-foreground mt-2">
                <a href={`mailto:${e.email}`} className="flex items-center gap-1 hover:text-foreground"><Mail size={12} /> {e.email}</a>
                {e.phone && <a href={`tel:${e.phone}`} className="flex items-center gap-1 hover:text-foreground"><Phone size={12} /> {e.phone}</a>}
                <span>· {new Date(e.created_at).toLocaleString()}</span>
              </div>
              {e.message && <div className="text-sm mt-3 p-3 rounded-md bg-secondary/40 border border-border">{e.message}</div>}
            </div>
            <div className="flex flex-col gap-2">
              {e.status !== "contacted" && <Button size="sm" variant="outline" onClick={() => setStatus(e.id, "contacted")} data-testid={`enq-${e.id}-contacted`}>Mark contacted</Button>}
              {e.status !== "closed" && <Button size="sm" onClick={() => setStatus(e.id, "closed")} data-testid={`enq-${e.id}-closed`}>Close</Button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
